import { useEffect, useRef, useState } from 'react'

import { useReducedMotion } from '../../UIComponents/Ghosts/ghostAnimation'
import { subscribeToProjectTaskExits } from './projectTaskCompletionSignal'
import { PROJECT_DISINTEGRATION_EXIT_HOLD_MS } from './projectDisintegrationMotion'

/** A small tail keeps React's final unmount from cutting off the last collapse frame. */
export const TASK_COMPLETION_PROJECT_EXIT_HOLD_MS = PROJECT_DISINTEGRATION_EXIT_HOLD_MS

/**
 * The row reports before the write that removes it (after any row-level completion/postpone
 * motion). Keep that fact just long enough for the open-task listener to decide the project is
 * empty, but not long enough for an unrelated later removal to borrow it.
 */
export const TASK_COMPLETION_PROJECT_EXIT_MEMORY_MS = 5000

const animationsAreDisabled = () => process.env.NODE_ENV === 'test'

/**
 * AT-2558 — holds a project card for the original Thanos-style disintegration when a genuine task
 * completion or postpone clears the project's visible work for today.
 *
 * The completion signal alone never starts or holds anything. The board must independently say the
 * complete project is leaving, which preserves immediate removals caused by filters, access changes
 * and non-completion actions. AT-2551's page-wide coloured sweep stays disconnected; this hook
 * supplies a run id only for the original mask, dust and sparks.
 */
export default function useTaskCompletionProjectExit({ projectId, enabled, lineWouldLeave }) {
    const reducedMotion = useReducedMotion()
    const active = enabled && !reducedMotion && !animationsAreDisabled()
    const [exitCandidate, setExitCandidate] = useState(null)
    const [previousLineWouldLeave, setPreviousLineWouldLeave] = useState(lineWouldLeave)
    const [exitRunId, setExitRunId] = useState(0)
    const [holding, setHolding] = useState(false)
    const consumedExitRef = useRef(null)

    useEffect(() => {
        if (!active) return undefined
        return subscribeToProjectTaskExits(projectId, event => {
            setExitCandidate({ taskId: event.taskId, reportedAt: Date.now() })
        })
    }, [active, projectId])

    // Render-phase adjustment keeps the card mounted continuously. An effect would run after the
    // commit that already removed it, producing a one-frame disappear/reappear flash.
    if (lineWouldLeave !== previousLineWouldLeave) {
        setPreviousLineWouldLeave(lineWouldLeave)
        const recentUnconsumedExit =
            active &&
            exitCandidate &&
            exitCandidate !== consumedExitRef.current &&
            Date.now() - exitCandidate.reportedAt <= TASK_COMPLETION_PROJECT_EXIT_MEMORY_MS

        if (lineWouldLeave && recentUnconsumedExit) {
            consumedExitRef.current = exitCandidate
            setExitRunId(runId => runId + 1)
            setHolding(true)
        } else if (!lineWouldLeave && holding) {
            // New work arrived before the dissolve finished. Stop holding immediately; the motion
            // layer receives the same verdict and restores the card in the same render cycle.
            setHolding(false)
        }
    }

    useEffect(() => {
        if (!holding) return undefined
        const timer = setTimeout(() => setHolding(false), TASK_COMPLETION_PROJECT_EXIT_HOLD_MS)
        return () => clearTimeout(timer)
    }, [exitRunId, holding])

    return {
        exitRunId,
        holdProjectLine: active && holding,
    }
}
